import type { EventDetail } from '@/lib/events';
import type { SimilarPerson } from '@/lib/staffing';
import { CONTACT_REF_KIND, type ContactPart, type ContactRef, type ContactRefKind, type TemplateContact } from '@/lib/templates';

/**
 * The people a note's contact field can be filled from: this event's
 * contacts first, then the staff directory and the team. Pure, so the order,
 * the labels and what a pick writes are tested without rendering.
 */

/** Where a choice comes from, in the words the picker shows. */
export const CONTACT_SOURCE_LABEL: Readonly<Record<ContactRefKind, string>> = {
  [CONTACT_REF_KIND.eventContact]: 'this event',
  [CONTACT_REF_KIND.salesforceContact]: 'this event',
  [CONTACT_REF_KIND.staffContact]: 'staff contact',
  [CONTACT_REF_KIND.user]: 'BCE team',
};

/** One person the picker offers. */
export interface ContactChoice {
  /** The Combobox value: the reference as one string. */
  readonly value: string;
  readonly label: string;
  readonly ref: ContactRef;
  /** The parts a pick writes into the note. */
  readonly parts: Readonly<Record<ContactPart, string>>;
}

/** A reference as the one string the Combobox selects by. */
export function contactChoiceValue(ref: ContactRef): string {
  return `${ref.kind}:${ref.id}`;
}

function choice(ref: ContactRef, parts: Readonly<Record<ContactPart, string>>, detail?: string): ContactChoice {
  const extra = detail?.trim() || parts.company;
  return {
    value: contactChoiceValue(ref),
    label: `${parts.name}${extra ? `, ${extra}` : ''} (${CONTACT_SOURCE_LABEL[ref.kind]})`,
    ref,
    parts,
  };
}

function text(value: string | undefined): string {
  return value?.trim() ?? '';
}

/**
 * This event's contacts: the Salesforce contacts on the opportunity, then the
 * ones the team added by hand. A hand-added contact with the same email as a
 * Salesforce one is the same person and is offered once.
 *
 * @param event - The event's contacts and account.
 * @returns The choices, in the order the event lists them.
 */
export function eventContactChoices(event: Pick<EventDetail, 'contacts' | 'localContacts' | 'account'>): ContactChoice[] {
  const choices: ContactChoice[] = [];
  const seenEmails = new Set<string>();
  const company = text(event.account?.name);
  for (const contact of event.contacts ?? []) {
    const email = text(contact.email).toLowerCase();
    const name = text(contact.name);
    // Salesforce contacts carry no id here; the email is how the app finds them.
    if (!email || !name || seenEmails.has(email)) continue;
    seenEmails.add(email);
    choices.push(
      choice(
        { kind: CONTACT_REF_KIND.salesforceContact, id: email },
        { name, company, email: text(contact.email), phone: text(contact.phone) || text(contact.cell) },
        contact.role,
      ),
    );
  }
  for (const contact of event.localContacts ?? []) {
    const email = text(contact.email).toLowerCase();
    if (email && seenEmails.has(email)) continue;
    if (email) seenEmails.add(email);
    choices.push(
      choice(
        { kind: CONTACT_REF_KIND.eventContact, id: contact._id },
        { name: text(contact.name), company: text(contact.company), email: text(contact.email), phone: text(contact.phone) || text(contact.cell) },
      ),
    );
  }
  return choices;
}

/**
 * The staff directory and the team with logins, as `listPeople` returns
 * them, leaving out anyone already offered from the event.
 *
 * @param people - Staff contacts and team members.
 * @param offered - The event's choices, to skip the same email twice.
 * @returns The choices, in the order given.
 */
export function directoryChoices(people: readonly SimilarPerson[], offered: readonly ContactChoice[] = []): ContactChoice[] {
  const seenEmails = new Set(offered.map((entry) => entry.parts.email.toLowerCase()).filter(Boolean));
  return people.flatMap((person) => {
    const email = text(person.email);
    if (email && seenEmails.has(email.toLowerCase())) return [];
    const kind = person.kind === 'user' ? CONTACT_REF_KIND.user : CONTACT_REF_KIND.staffContact;
    return [choice({ kind, id: person._id }, { name: text(person.name) || email, company: '', email, phone: text(person.phone) })];
  });
}

/**
 * The note's contact value for a pick: the person's parts as they are now,
 * and the reference to who it was.
 *
 * @param picked - The chosen person.
 * @returns The value to store.
 */
export function contactValueFor(picked: ContactChoice): TemplateContact {
  return { ...picked.parts, contactRef: picked.ref };
}

/**
 * The value after one part is typed by hand. The reference goes: the note no
 * longer mirrors that person.
 *
 * @param current - The stored value.
 * @param part - The part being edited.
 * @param next - Its new text.
 * @returns The value to store.
 */
export function withTypedPart(current: TemplateContact, part: ContactPart, next: string): TemplateContact {
  const { contactRef: _ref, personId: _legacy, ...parts } = current;
  return { ...parts, [part]: next };
}

/**
 * Where a linked contact's own record lives in the app, if it has one. An
 * event's hand-added contacts live on the event itself.
 *
 * @param ref - The reference.
 * @returns The app path, or undefined.
 */
export function contactRefPath(ref: ContactRef): string | undefined {
  switch (ref.kind) {
    case CONTACT_REF_KIND.salesforceContact:
      return `/contacts/by-email/${encodeURIComponent(ref.id)}`;
    case CONTACT_REF_KIND.staffContact:
      return `/staff-contacts/${ref.id}`;
    case CONTACT_REF_KIND.user:
      return `/people/${ref.id}`;
    case CONTACT_REF_KIND.eventContact:
      return undefined;
    default: {
      const exhaustive: never = ref.kind;
      return exhaustive;
    }
  }
}
